/**
 * Clipboard utility with fallback for restricted contexts
 * 
 * The modern Clipboard API (navigator.clipboard.writeText) may be blocked
 * in certain contexts due to permissions policies. This utility provides
 * a fallback using the legacy execCommand approach, and a final fallback
 * that returns the text for manual display.
 */

/**
 * Copy text to clipboard with fallback support
 * @param {string} text - Text to copy
 * @returns {Promise<{success: boolean, text?: string}>} - success true if copied, or text if needs manual copy
 */
export async function copyToClipboard(text) {
  // Try modern Clipboard API first
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return { success: true };
    } catch (err) {
      // Clipboard API blocked, fall through to fallback
      console.warn('Clipboard API blocked, trying fallback:', err.message);
    }
  }

  // Fallback: Create a temporary textarea and use execCommand
  const fallbackResult = fallbackCopyToClipboard(text);
  if (fallbackResult) {
    return { success: true };
  }
  
  // All methods failed - return text for manual copy
  console.warn('All clipboard methods failed, returning text for manual copy');
  return { success: false, text };
}

/**
 * Fallback copy method using execCommand
 * @param {string} text - Text to copy
 * @returns {boolean} - True if successful
 */
function fallbackCopyToClipboard(text) {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  
  // Make it visible but off-screen (some browsers require visibility)
  textArea.style.position = 'fixed';
  textArea.style.top = '-9999px';
  textArea.style.left = '-9999px';
  textArea.style.width = '1px';
  textArea.style.height = '1px';
  textArea.style.opacity = '0.01';
  
  document.body.appendChild(textArea);
  
  // iOS Safari specific handling
  const range = document.createRange();
  range.selectNodeContents(textArea);
  const selection = window.getSelection();
  selection.removeAllRanges();
  
  // Try both selection methods
  textArea.focus();
  textArea.select();
  textArea.setSelectionRange(0, text.length); // For mobile
  
  let success = false;
  try {
    success = document.execCommand('copy');
  } catch (err) {
    console.error('execCommand copy failed:', err);
  }
  
  selection.removeAllRanges();
  document.body.removeChild(textArea);
  return success;
}

export default { copyToClipboard };
